import { formatAttributionPageLabel } from '@/lib/campaign-page-url'
import type { CampaignUrlAttributionRow } from '@/types/campaign-report'

export const UNIDENTIFIED_ORIGIN_DISPLAY = 'Origem não identificada'

export type UrlAttributionConversionFilter =
  | 'all'
  | 'with_sales'
  | 'without_sales'
  | 'with_views'
  | 'conversion_gt_0'
  | 'conversion_gte_10'
  | 'conversion_gte_25'
  | 'conversion_gte_50'

export type UrlAttributionSortKey = 'page' | 'views' | 'sales' | 'conversion_rate' | 'revenue'

export type UrlAttributionSortDirection = 'asc' | 'desc'

export const MAX_URL_ATTRIBUTION_SEARCH_TERMS = 12
export const MAX_URL_ATTRIBUTION_SEARCH_TERM_LENGTH = 80

export const URL_ATTRIBUTION_CONVERSION_FILTERS: ReadonlyArray<{
  value: UrlAttributionConversionFilter
  label: string
}> = [
  { value: 'all', label: 'Todas as origens' },
  { value: 'with_sales', label: 'Com vendas' },
  { value: 'without_sales', label: 'Sem vendas' },
  { value: 'with_views', label: 'Com visualizações' },
  { value: 'conversion_gt_0', label: 'Conversão acima de zero' },
  { value: 'conversion_gte_10', label: 'Conversão a partir de 10%' },
  { value: 'conversion_gte_25', label: 'Conversão a partir de 25%' },
  { value: 'conversion_gte_50', label: 'Conversão a partir de 50%' },
]

function matchesConversionFilter(
  row: CampaignUrlAttributionRow,
  conversionFilter: UrlAttributionConversionFilter
): boolean {
  switch (conversionFilter) {
    case 'with_sales':
      return row.sales > 0
    case 'without_sales':
      return row.sales === 0
    case 'with_views':
      return row.views > 0
    case 'conversion_gt_0':
      return row.conversion_rate > 0
    case 'conversion_gte_10':
      return row.conversion_rate >= 10
    case 'conversion_gte_25':
      return row.conversion_rate >= 25
    case 'conversion_gte_50':
      return row.conversion_rate >= 50
    default:
      return true
  }
}

export function normalizeUrlAttributionSearchTerm(value: string): string {
  const normalized = value.trim().replace(/\s+/g, ' ')
  if (normalized.length <= MAX_URL_ATTRIBUTION_SEARCH_TERM_LENGTH) {
    return normalized
  }
  return normalized.slice(0, MAX_URL_ATTRIBUTION_SEARCH_TERM_LENGTH)
}

export function splitUrlAttributionSearchDraft(value: string): string[] {
  return value
    .split(',')
    .map((part) => normalizeUrlAttributionSearchTerm(part))
    .filter((term) => term !== '')
}

export function hasUrlAttributionSearchDraft(value: string): boolean {
  return splitUrlAttributionSearchDraft(value).length > 0
}

export function addUrlAttributionSearchTerms(
  currentTerms: readonly string[],
  candidate: string
): string[] {
  const nextTerms = [...currentTerms]
  const seen = new Set(nextTerms.map((term) => term.toLowerCase()))

  for (const term of splitUrlAttributionSearchDraft(candidate)) {
    const key = term.toLowerCase()
    if (seen.has(key) || nextTerms.length >= MAX_URL_ATTRIBUTION_SEARCH_TERMS) {
      continue
    }
    seen.add(key)
    nextTerms.push(term)
  }

  return nextTerms
}

export function shouldClearUrlAttributionSearchDraft(
  currentTerms: readonly string[],
  candidate: string
): boolean {
  const incoming = splitUrlAttributionSearchDraft(candidate)
  if (incoming.length === 0) {
    return true
  }

  const nextTerms = addUrlAttributionSearchTerms(currentTerms, candidate)
  if (nextTerms.length > currentTerms.length) {
    return true
  }

  const seen = new Set(currentTerms.map((term) => term.toLowerCase()))
  return incoming.every((term) => seen.has(term.toLowerCase()))
}

export function resolveUrlAttributionSearchTerms(
  committedTerms: readonly string[],
  draft: string
): string[] {
  return addUrlAttributionSearchTerms(committedTerms, draft)
}

export function filterUrlAttributionRows(
  rows: CampaignUrlAttributionRow[],
  conversionFilter: UrlAttributionConversionFilter,
  searchTerms: readonly string[]
): CampaignUrlAttributionRow[] {
  const activeTerms = searchTerms
    .map((term) => normalizeUrlAttributionSearchTerm(term).toLowerCase())
    .filter((term) => term !== '')

  return rows.filter((row) => {
    if (!matchesConversionFilter(row, conversionFilter)) {
      return false
    }
    if (activeTerms.length === 0) {
      return true
    }
    const haystack = formatAttributionPageLabel(row.page_url_display).toLowerCase()
    return activeTerms.some((term) => haystack.includes(term))
  })
}

export function sortUrlAttributionRows(
  rows: CampaignUrlAttributionRow[],
  sortKey: UrlAttributionSortKey,
  direction: UrlAttributionSortDirection
): CampaignUrlAttributionRow[] {
  const factor = direction === 'asc' ? 1 : -1

  return [...rows].sort((left, right) => {
    if (sortKey === 'page') {
      return (
        formatAttributionPageLabel(left.page_url_display).localeCompare(
          formatAttributionPageLabel(right.page_url_display),
          'pt-BR',
          { sensitivity: 'base' }
        ) * factor
      )
    }

    return (left[sortKey] - right[sortKey]) * factor
  })
}

export function applyUrlAttributionView(
  rows: CampaignUrlAttributionRow[],
  conversionFilter: UrlAttributionConversionFilter,
  searchTerms: readonly string[],
  sortKey: UrlAttributionSortKey,
  direction: UrlAttributionSortDirection
): CampaignUrlAttributionRow[] {
  return sortUrlAttributionRows(
    filterUrlAttributionRows(rows, conversionFilter, searchTerms),
    sortKey,
    direction
  )
}

export function summarizeUrlAttributionRows(rows: CampaignUrlAttributionRow[]): {
  origins: number
  views: number
  sales: number
  revenue: number
} {
  return rows.reduce(
    (acc, row) => ({
      origins: acc.origins + 1,
      views: acc.views + row.views,
      sales: acc.sales + row.sales,
      revenue: acc.revenue + row.revenue,
    }),
    { origins: 0, views: 0, sales: 0, revenue: 0 }
  )
}

export function formatAttributionPeriodLabel(dateFrom: string, dateTo: string): string {
  const formatPart = (value: string): string => {
    const [year, month, day] = value.split('-')
    if (!year || !month || !day) {
      return value
    }
    return `${day}/${month}/${year}`
  }

  return `${formatPart(dateFrom)} a ${formatPart(dateTo)}`
}

export function resolveUrlChartTooltipLabel(fallback: unknown, payload: unknown): string {
  if (!Array.isArray(payload) || payload.length === 0) {
    return String(fallback ?? '')
  }

  const first = payload[0]
  if (!first || typeof first !== 'object' || !('payload' in first)) {
    return String(fallback ?? '')
  }

  const row = first.payload
  if (!row || typeof row !== 'object' || !('page_url_display' in row)) {
    return String(fallback ?? '')
  }

  const display = row.page_url_display
  if (typeof display === 'string' && display.trim() !== '') {
    return formatAttributionPageLabel(display)
  }

  return String(fallback ?? '')
}

export function isUnidentifiedAttributionOrigin(pageUrlDisplay: string): boolean {
  return formatAttributionPageLabel(pageUrlDisplay).trim() === UNIDENTIFIED_ORIGIN_DISPLAY
}

export function isUrlAttributionRowActiveOnChart(
  pageUrlDisplay: string,
  visibilityOverrides: Readonly<Record<string, boolean>>
): boolean {
  if (Object.prototype.hasOwnProperty.call(visibilityOverrides, pageUrlDisplay)) {
    return visibilityOverrides[pageUrlDisplay] === true
  }

  return !isUnidentifiedAttributionOrigin(pageUrlDisplay)
}

export type ChartVisibilityHeaderState = 'all' | 'none' | 'some'

export function resolveChartVisibilityHeaderState(
  pageUrlDisplays: readonly string[],
  visibilityOverrides: Readonly<Record<string, boolean>>
): ChartVisibilityHeaderState {
  if (pageUrlDisplays.length === 0) {
    return 'none'
  }

  let activeCount = 0
  for (const pageUrlDisplay of pageUrlDisplays) {
    if (isUrlAttributionRowActiveOnChart(pageUrlDisplay, visibilityOverrides)) {
      activeCount += 1
    }
  }

  if (activeCount === 0) {
    return 'none'
  }
  if (activeCount === pageUrlDisplays.length) {
    return 'all'
  }
  return 'some'
}

export function applyChartVisibilityToDisplays(
  visibilityOverrides: Readonly<Record<string, boolean>>,
  pageUrlDisplays: readonly string[],
  isActive: boolean
): Record<string, boolean> {
  const nextOverrides: Record<string, boolean> = { ...visibilityOverrides }
  for (const pageUrlDisplay of pageUrlDisplays) {
    nextOverrides[pageUrlDisplay] = isActive
  }
  return nextOverrides
}
